import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import type { PlacePick } from './composer-inserts';

/**
 * One typeahead row, as `/api/places?search=` returns it.
 *
 * It is NOT a `PlacePick` — a prediction has no coordinates. That is the point of the two-step: Atlas
 * geocodes the one row the user picks (`resolvePlace`), not every row it offers. `matched` indexes into
 * `mainText`, so the row can bold exactly the characters the user typed rather than re-searching the
 * string itself, which gets the wrong answer for any accent-folded or transliterated match.
 */
export interface PlacePrediction {
  searchId: string;
  label: string;
  mainText: string;
  secondaryText: string;
  matched: { offset: number; length: number }[];
  distanceMeters?: number;
}

/** Client-local shape for a resolved place — picking a row eventually hands one of these to the composer. */
export type PlaceSummary = PlacePick;

/**
 * One character cannot produce a useful prediction anywhere in the world — it matches a continent before
 * it matches a street. The server floors this too; flooring it here as well means a single keystroke
 * costs no request at all. Wix's other Atlas consumers guard the same way.
 */
export const MIN_PLACE_QUERY_LENGTH = 2;

export interface PlaceSearchOptions {
  /** The APP language, so place names come back in the language the rest of the UI is in. */
  language?: string;
  /**
   * The picker session: one token for every keystroke of a search plus the pick that ends it. Nullable
   * because it lives in a ref on the caller's side; a missing token just means no session is declared.
   */
  sessionToken?: string | null;
}

function withOptions(params: URLSearchParams, { language, sessionToken }: PlaceSearchOptions): URLSearchParams {
  if (language) params.set('language', language);
  if (sessionToken) params.set('sessionToken', sessionToken);
  return params;
}

export async function fetchPlacePredictions(search: string, options: PlaceSearchOptions = {}): Promise<PlacePrediction[]> {
  if (search.length < MIN_PLACE_QUERY_LENGTH) return [];
  const params = withOptions(new URLSearchParams({ search }), options);
  const res = await apiFetch(`${API_BASE}/api/places?${params}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as PlacePrediction[] | undefined;
  return Array.isArray(data) ? data : [];
}

/** Thrown when the picked prediction's `searchId` has aged out — the user has to search again. */
export class PlaceExpiredError extends Error {
  constructor() {
    super('place_expired');
    this.name = 'PlaceExpiredError';
  }
}

/**
 * Turn the prediction the user picked into the coordinates the composer needs.
 *
 * Deliberately not a hook: this fires once, on a click, and its result is consumed immediately rather
 * than rendered — react-query would only add a cache entry nothing ever reads again.
 */
export async function resolvePlace(searchId: string, options: PlaceSearchOptions = {}): Promise<PlaceSummary> {
  const params = withOptions(new URLSearchParams({ searchId }), options);
  const res = await apiFetch(`${API_BASE}/api/places?${params}`, { credentials: 'include' });
  if (res.status === 404) {
    throw new PlaceExpiredError();
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as PlaceSummary;
}

export type PlaceSearchState =
  | { status: 'loading'; predictions: PlacePrediction[] }
  | { status: 'ready'; predictions: PlacePrediction[] }
  | { status: 'error' };

/**
 * The search box passes an already-debounced query. Below the two-character floor → an empty list and no
 * request (place search is free-text only; there's no recent-places default like contacts have).
 *
 * The session token is NOT part of the cache key: it identifies the picker session, not the query, so
 * folding it in would miss the cache on every re-open for a query we just answered. The language IS part
 * of it — the same query returns different place names in a different language.
 */
export function usePlaceSearch(search: string, options: PlaceSearchOptions = {}): PlaceSearchState {
  const { language, sessionToken } = options;
  const enabled = search.length >= MIN_PLACE_QUERY_LENGTH;
  const query = useQuery({
    queryKey: queryKeys.places(search, language),
    queryFn: () => fetchPlacePredictions(search, { language, sessionToken }),
    enabled,
    staleTime: 60_000,
  });

  if (!enabled) return { status: 'ready', predictions: [] };
  if (query.data) return { status: 'ready', predictions: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading', predictions: [] };
}
